import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useLoaderData, useActionData, useSubmit } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import Page from 'client/components/aria/Page/Page';
import SortButton from 'client/components/aria/SortButton/SortButton';
import Table from 'client/components/aria/Table';
import { Cell, Column, Item, Row, TableBody, TableHeader } from 'react-stately';
import Menu from 'client/components/aria/Menu/Menu';
import DeleteDialog from 'client/components/aria/Dialog/DeleteDialog/DeleteDialog';
import Pagination from 'client/components/aria/Pagination/Pagination';
import ShowResults from 'client/components/aria/ShowResults/ShowResults';
import Button from 'client/components/aria/Button/Button';
import Search from 'client/components/aria/Search/Search';
import Alert from 'client/components/aria/Alert/Alert';
import moment from 'moment';

import styles from './ListObservationsPage.module.scss';
import Icon from '@mdi/react';
import { mdiDelete, mdiDotsVertical } from '@mdi/js';

import {
    VIEW_OBSERVATION_PAGE,
    SAVE_OBSERVATION_PAGE,
    OBSERVATIONS_PAGE
} from 'client/utils/routes/routeConstants';

import { permissions } from 'client/utils/constants/permissions';
import NavLink from 'client/components/aria/NavLink/NavLink';
import { useHasUserPermission } from 'client/utils/auth/userPermissions';
import { useLocaleAutoSwitch } from 'i18n/localeUtils';
import { VisuallyHidden } from 'react-aria';

const MENU_ACTION = {
    VIEW_OBSERVATION: 'VIEW_OBSERVATION',
    EDIT_OBSERVATION: 'EDIT_OBSERVATION',
    DELETE_OBSERVATION: 'DELETE_OBSERVATION'
};

const ListObservationPage = () => {
    const intl = useIntl();
    useLocaleAutoSwitch();
    const loaderData = useLoaderData();
    const actionData = useActionData();
    const { content, totalPages } = loaderData.observations;
    const navigate = useNavigate();
    const location = useLocation();
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deletionDetails, setDeletionDetails] = useState({});
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const submit = useSubmit();
    const hasPermissionToUpdateObservations = useHasUserPermission(permissions.OBSERVATIONS_UPDATE);

    const onTableMenuAction = (evt, observation) => {
        if (evt === MENU_ACTION.VIEW_OBSERVATION) {
            navigate(VIEW_OBSERVATION_PAGE + '/' + observation.systemId);
        } else if (evt === MENU_ACTION.EDIT_OBSERVATION) {
            navigate(SAVE_OBSERVATION_PAGE + '/' + observation.systemId);
        } else if (evt === MENU_ACTION.DELETE_OBSERVATION) {
            setDeletionDetails({ title: observation.objectName, ids: [observation.systemId] });
            setIsDeleteOpen(true);
        }
    };

    const isItemsSelected = selectedKeys.size > 0 || selectedKeys === 'all';
    const userCanDelete = useHasUserPermission(permissions.OBSERVATIONS_DELETE);

    useEffect(() => {
        // Resets all temporary state when the content changes, such as search
        setDeletionDetails({});
        setSelectedKeys(new Set());
    }, [content]);

    const onDeletePress = () => {
        if (!isItemsSelected) {
            return;
        }
        const ids = (selectedKeys === 'all') ? content.map(observation => observation.systemId) : Array.from(selectedKeys);
        let title;
        if (ids.length === 1) {
            title = content.find(observation => observation.systemId === ids[0]).objectName;
        } else {
            title = ids.length + ' ' + intl.formatMessage({ id: 'listObservationsPage.items' });
        }
        setDeletionDetails({
            title,
            ids
        });
        setIsDeleteOpen(true);
    };

    const onDelete = () => {
        const formData = new FormData();
        formData.set('systemIds', JSON.stringify(deletionDetails.ids));
        submit(formData, {
            method: 'DELETE',
            action: OBSERVATIONS_PAGE
        });
    };

    const params = new URLSearchParams(location.search);
    const currentPage = parseInt(params.get('page')) + 1 || 1;
    const onPaginationChange = page => {
        const params = new URLSearchParams(location.search);
        params.set('page', page - 1);
        navigate(location.pathname + '?' + params.toString());
    };

    return (
        <Page
            title={intl.formatMessage({ id: 'listObservationsPage.page.title' })}
            role="main"
            data-testid="listObservationsPage"
            className={styles.page}
        >
            {actionData?.deleted
                ? <div className="govuk-grid-row">
                    <div className="govuk-grid-column-full govuk-!-margin-bottom-6">
                        <Alert data-testid="listObservationsPage.delete.confirmation" variant="warning" title={intl.formatMessage({ id: 'listObservationsPage.page.title' })}>
                            <h2 className="govuk-error-summary__title govuk-!-margin-0">
                                {intl.formatMessage({ id: 'listObservationsPage.observationsDeleted' })}
                            </h2>
                        </Alert>
                    </div>
                </div>
                : null
            }
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-full">
                    <h1 className={['govuk-heading-xl', styles.header].join(' ')}>{intl.formatMessage({ id: 'listObservationsPage.page.title' })}</h1>
                    <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible"/>
                </div>
            </div>
            <div className={'govuk-grid-row'}>
                <div className="govuk-grid-column-full">
                    <Search
                        data-testid='observationspage'
                        totalElements={loaderData.observations.totalElements}
                        size={loaderData.observations.size}
                    />
                    {(isItemsSelected && userCanDelete) &&
                        <div className={'govuk-grid-column-one-half'}>
                            <Button
                                data-testid={'delete'}
                                variant={'error'}
                                onPress={onDeletePress}
                            >
                                <Icon path={mdiDelete} size={0.7}/>
                                {' '}
                                {intl.formatMessage({ id: 'delete' })}
                            </Button>
                        </div>
                    }
                </div>
                <div className={['govuk-grid-column-full', styles.table].join(' ')}>
                    <Table
                        aria-label={intl.formatMessage({ id: 'listObservationsPage.page.title' })}
                        selectionMode="multiple"
                        data-testid="listObservationsPage"
                        selectedKeys={selectedKeys}
                        onSelectionChange={setSelectedKeys}
                    >
                        <TableHeader>
                            <Column>
                                <SortButton column={'objectName'}>
                                    {intl.formatMessage({ id: 'name' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'observationCategory'}>
                                    {intl.formatMessage({ id: 'listObservationsPage.type' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <SortButton column={'dateLastUpdated'}>
                                    {intl.formatMessage({ id: 'listObservationsPage.page.dateModified' })}
                                </SortButton>
                            </Column>
                            <Column>
                                <VisuallyHidden><FormattedMessage id="menu" /></VisuallyHidden>
                            </Column>
                        </TableHeader>
                        <TableBody>
                            {content.map(observation => {
                                return <Row key={observation.systemId}>
                                    <Cell wrapText><NavLink data-testid='listObservationsPage.link' to={VIEW_OBSERVATION_PAGE + '/' + observation.systemId}>{observation.objectName}</NavLink></Cell>
                                    <Cell wrapText>{observation.observationCategory}</Cell>
                                    <Cell>{moment(observation.dateLastUpdated).format('lll')}</Cell>
                                    <Cell horzAlign="middle">
                                        <Menu
                                            aria-label={`${observation.objectName} ${intl.formatMessage({ id: 'menu' })}`}
                                            icon={<Icon path={mdiDotsVertical} size={1}/>}
                                            onAction={evt => onTableMenuAction(evt, observation)}
                                            placement="bottom right"
                                        >
                                            <Item
                                                key={MENU_ACTION.VIEW_OBSERVATION}
                                                aria-label={intl.formatMessage({ id: 'listObservationsPage.viewPage' }) + ' ' + observation.objectName}
                                            >
                                                {intl.formatMessage({ id: 'listObservationsPage.viewPage' })}
                                            </Item>
                                            {hasPermissionToUpdateObservations
                                                ? <Item
                                                    key={MENU_ACTION.EDIT_OBSERVATION}
                                                    aria-label={intl.formatMessage({ id: 'listObservationsPage.edit' }) + ' ' + observation.objectName}
                                                >
                                                    {intl.formatMessage({ id: 'listObservationsPage.edit' })}
                                                </Item>
                                                : null
                                            }
                                            {userCanDelete
                                                ? <Item
                                                    key={MENU_ACTION.DELETE_OBSERVATION}
                                                    aria-label={intl.formatMessage({ id: 'delete' }) + ' ' + observation.objectName}
                                                >
                                                    {intl.formatMessage({ id: 'delete' })}
                                                </Item>
                                                : null}
                                        </Menu>
                                    </Cell>
                                </Row>;
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <div className="govuk-grid-row">
                <div className={['govuk-grid-column-full', styles.pagination].join(' ')}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={onPaginationChange}
                        data-testid="pagination"
                    />
                </div>
                <div className="govuk-grid-column-full govuk-!-text-align-centre">
                    <ShowResults
                        totalElements={loaderData.observations.totalElements}
                        size={loaderData.observations.size}
                    />
                </div>
            </div>
            <DeleteDialog
                data-testid={'listObservationsPage.deleteDialog'}
                isOpen={isDeleteOpen}
                setIsOpen={setIsDeleteOpen}
                onDelete={onDelete}
                title={deletionDetails.title}
            />
        </Page>
    );
};

export default ListObservationPage;
